import * as React from 'react';
import * as querystring from 'query-string';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FormData } from '../components/ccpa/do-not-sell/Form';
import CCPAVerifyResidency from '../components/ccpa/VerifyResidency';
import CCPADoNotSellForm from '../components/ccpa/do-not-sell/Form';
import CCPASubmissionSuccess from '../components/ccpa/do-not-sell/Confirmation';
import { RouteComponentProps } from 'react-router-dom';

import { Privacy, ApiResponse, PrivacyApi } from '@weddingspot/ws-api-client';
import { FormikHelpers as FormikActions } from 'formik';

enum Step {
    RESIDENCY = 1,
    FORM = 2,
    SUCCESS = 3,
}

interface State {
    curStep: Step;
}

interface Props extends RouteComponentProps {}

export class CCPADoNotSellContainer extends React.Component<Props, State> {
    readonly state: State = {
        curStep: Step.RESIDENCY,
    };

    public constructor(props: Props) {
        super(props);
        this.handleResidencyVerification = this.handleResidencyVerification.bind(this);
        this.navigateBackToPrevious = this.navigateBackToPrevious.bind(this);
        this.handleFormSubmission = this.handleFormSubmission.bind(this);
    }

    public render() {
        return (
            <div>
                <Helmet>
                    <title>Wedding Spot | Do not sell my information</title>
                </Helmet>
                {this.getComponentForStep(this.state.curStep)}
            </div>
        );
    }

    private getComponentForStep(step: Step) {
        if (step === Step.RESIDENCY) {
            return <CCPAVerifyResidency onVerification={this.handleResidencyVerification} />;
        } else if (step === Step.FORM) {
            return (
                <CCPADoNotSellForm
                    initialValues={this.getFormInitial()}
                    handleOnCancelClick={this.navigateBackToPrevious}
                    handleOnSubmit={this.handleFormSubmission}
                />
            );
        } else {
            return (
                <CCPASubmissionSuccess
                    confirmationText="We've received your request to not sell your information"
                    onConfirm={this.navigateBackToPrevious}
                />
            );
        }
    }

    private handleFormSubmission(formData: FormData, formikActions: FormikActions<Partial<FormData>>) {
        const request: Privacy.CCPADoNotSellRequest = {
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
        };

        formikActions.setSubmitting(true);

        PrivacyApi.CCPADoNotSellRequest(request)
            .then(() => {
                this.setState({ curStep: Step.SUCCESS });
            })
            .catch((err: ApiResponse<Partial<Privacy.CCPADoNotSellRequest>>) => {
                if (err.status === 0) {
                    formikActions.setErrors({
                        email: !!err.content.email ? err.content.email[0] : undefined,
                        firstName: !!err.content.first_name ? err.content.first_name[0] : undefined,
                        lastName: !!err.content.last_name ? err.content.last_name[0] : undefined,
                    });
                } else {
                    formikActions.setStatus(
                        'An unknown error occurred, please try again later' + ' or reach out to support at concierge@wedding-spot.com'
                    );
                }
            })
            .then(() => {
                formikActions.setSubmitting(false);
            });
    }

    private handleResidencyVerification(verified: boolean) {
        if (verified) {
            this.setState({ curStep: Step.FORM });
        } else {
            this.navigateBackToPrevious();
        }
    }

    private navigateBackToPrevious() {
        this.props.history.goBack();
    }

    private getFormInitial() {
        const { email, firstName, lastName } = this.getQueryParams() as Record<string, string>;
        let formInitial = {
            email: '',
            firstName: '',
            lastName: '',
        };

        if (!!email) {
            formInitial.email = email;
        }
        if (!!firstName) {
            formInitial.firstName = firstName;
        }
        if (!!lastName) {
            formInitial.lastName = lastName;
        }
        return formInitial;
    }

    private getQueryParams() {
        const { email = '', firstName = '', lastName = '' } = querystring.parse(this.props.location.search);
        return { email: sanitize(email), firstName: sanitize(firstName), lastName: sanitize(lastName) };
    }
}

const sanitize = (value: any): string => {
    const sanitizer = document.createElement('div');
    sanitizer.innerHTML = value;
    return sanitizer.textContent || '';
};

export default withRouter(CCPADoNotSellContainer);
